/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable react/destructuring-assignment */
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import IconButton from 'Components/UI/BehavioralFormElement/Button/IconButton';
import CurrencyFormat from 'Components/Format/Currency';
import DataTable from 'Components/UI/DataTable';
import PagingLinksWithViewsButtons from 'Components/UI/DataTable/PagingLinksWithViewsButtons';
import Icon from 'Components/UI/Icon';
import { openModal } from 'Helpers/ModalHelper';
import modalConsts from 'Consts/ModalNames';
import {
  addSpacesAndCapitalizeAllWords,
  formatPhoneNumber,
} from 'Helpers/Formats';
import { planningUri, clientsUri } from 'Consts/advisorUris';

import { encodeQuery, decodeQuery } from 'Helpers/Query';
import './index.css';
import history from 'State/history';
import moment from 'moment';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import Filters from './Filters';

const filterName = ({ id, value }, row) => {
  const displayName = `${row[id].firstName || ''} ${row[id].lastName
  || ''} ${row[id].id || ''}`
    .trim()
    .toLowerCase();
  return displayName.indexOf(value.toLowerCase()) > -1;
};

const sortByNamePart = (aName, bName) => {
  const normalizedAName = aName || '';
  const normalizedBName = bName || '';
  if (normalizedAName > normalizedBName) {
    return 1;
  }
  if (normalizedAName < normalizedBName) {
    return -1;
  }
  return 0;
};

const sortByName = (
  { firstName: aFirstName, lastName: aLastName },
  { firstName: bFirstName, lastName: bLastName },
) => {
  let comparison = sortByNamePart(aLastName, bLastName);
  if (comparison === 0) {
    comparison = sortByNamePart(aFirstName, bFirstName);
  }
  return comparison;
};

const sortByDate = (a, b) => {
  if (!a) return -1;

  if (!b) return 1;

  if (a.isBefore(b)) return -1;

  if (a.isAfter(b)) return 1;

  return 0;
};

const filterByContains = ({ id, value }, row) => row[id]
  .toString()
  .toLowerCase()
  .indexOf(value.toLowerCase()) > -1;

const filterByContainsPhoneNumber = ({ id, value }, row) => {
  const phoneNumber = row[id].toString().toLowerCase();
  if (phoneNumber.indexOf(value.toLowerCase()) > -1) {
    return true;
  }
  return formatPhoneNumber(phoneNumber).indexOf(value.toLowerCase()) > -1;
};

const filterCurrency = ({ id, value }, row) => {
  if (value !== '' && (row[id] === null || row[id] === undefined)) return false;
  const normalizedValue = value.replace(/(^\$)|,/g, '');
  return row[id].toString().indexOf(normalizedValue) > -1;
};

const filterDate = ({ id, value }, row) => {
  if (!row[id]) return false;
  return row[id].format('MM/DD/YY').indexOf(value) > -1;
};

// eslint-disable-next-line react/prop-types
const renderName = ({ value: { id, firstName, lastName } }) => (id ? (
  <span>{`${firstName || ''} ${lastName || ''} [${id}]`.trim()}</span>
) : (
  <span>{`${firstName || ''} ${lastName || ''}`.trim()}</span>
));

// eslint-disable-next-line react/prop-types
const renderWorkgroupName = ({ value: { workgroupName } }) => (
  <span>{`${workgroupName || ''}`.trim()}</span>
);

const renderClient = init => (
  <Fragment>
    <div className="view">
      <Icon icon="zoom" />
      <span>View</span>
    </div>
    {renderName(init)}
  </Fragment>
);

const renderDate = ({ value }) => {
  if (!value) return null;

  if (moment.isMoment(value)) {
    return value.format('MM/DD/YY');
  }
  const m = moment(value);
  if (m.isValid()) {
    return m.format('MM/DD/YY');
  }
  return null;
};

const updateQueryString = (qs) => {
  // eslint-disable-line
  history.push({
    pathname: clientsUri,
    search: qs,
  });
};

const columns = [
  {
    accessor: d => ({
      firstName: d.attr('clientFirstName'),
      lastName: d.attr('clientLastName'),
      id: d.id(),
    }),
    Cell: renderClient,
    filterMethod: filterName,
    Header: 'Name',
    id: 'clientName',
    sortMethod: sortByName,
    width: 250,
  },
  {
    accessor: d => ({
      workgroupName: d.attr('workgroupName'),
    }),
    Cell: renderWorkgroupName,
    filterMethod: filterName,
    Header: 'Planner',
    id: 'lpName',
    sortMethod: sortByName,
    width: 200,
  },
  {
    accessor: d => {
      return d.attr('clientEmail');
    },
    filterMethod: filterByContains,
    Header: 'Email',
    id: 'clientEmail',
    width: 300,
  },
  {
    accessor: d => {
      return d.attr('statusName');
    },
    id: 'statusName',
    Header: 'Status',
    width: 100,
  },
  {
    accessor: (resource) => {
      const nextMeetingDate = resource.attr('nextMeetingDate');

      return nextMeetingDate && moment(nextMeetingDate);
    },
    Cell: renderDate,
    filterMethod: filterDate,
    Header: 'Next Meeting Date',
    id: 'milestoneDate',
    width: 125,
    sortMethod: sortByDate,
  },
  {
    accessor: (resource) => {
      const lastMeetingDate = resource.attr('lastMeetingDate');

      return lastMeetingDate && moment(lastMeetingDate);
    },
    Cell: renderDate,
    filterMethod: filterDate,
    Header: 'Last Meeting Date',
    id: 'lastMeetingDate',
    width: 125,
    sortMethod: sortByDate,
  },
  {
    accessor: d => {
      return d.attr('clientPhoneNumber');
    },
    Header: 'PhoneNumber',
    filterMethod: filterByContainsPhoneNumber,
    Cell: ({ value }) => formatPhoneNumber(value),
    id: 'clientPhoneNumber',
    width: 180,
  },
  {
    Header: 'Service Level',
    accessor: d => {
      return d.attr('annualFee');
    },
    id: 'annualFee',
    filterMethod: filterCurrency,
    // eslint-disable-next-line react/prop-types
    Cell: ({ value }) => (value && value > 0 ? <CurrencyFormat amount={value} /> : null),
    width: 120,
  },
  {
    Header: 'Escalation Type',
    accessor: d => {
      return d.attr('escalationType');
    },
    id: 'escalationType',
    Cell: ({ value }) => addSpacesAndCapitalizeAllWords(value),
    width: 120,
  },
].map(h => ({ ...h, minWidth: null }));

const sortData = data => data.sort((a, b) => sortByName(
  {
    firstName: a.attr('clientFirstName'),
    lastName: a.attr('clientLastName'),
  },
  {
    firstName: b.attr('clientFirstName'),
    lastName: b.attr('clientLastName'),
  },
));

function filterHouseholdSummaries(coll, filters) {
  if (!coll) {
    return [];
  }

  const {
    isBeta: isBetaFilter,
    paymentMethod: paymentMethodFilter,
    lastMeetingDate: lastMeetingDateFilter,
    signatureDate: signatureDateFilter,
    status: statusFilter,
    escalated: escalatedFilter,
  } = filters;

  const filteredResources = coll.resources().list().filter(
    (resource) => {
      const {
        isBeta,
        paymentMethod,
        lastMeetingDate,
        signatureDate,
        statusName,
        escalated,
      } = resource.attributes();

      if (paymentMethodFilter && paymentMethod !== paymentMethodFilter) {
        return false;
      }

      if (statusFilter.length > 0 && statusFilter.indexOf(statusName) < 0) {
        return false;
      }

      if (lastMeetingDateFilter) {
        if (lastMeetingDate) {
          const momentMD = moment(lastMeetingDate);
          const [startValue, endValue] = lastMeetingDateFilter;
          return momentMD.isBefore(endValue) && momentMD.isAfter(startValue);
        }

        return false;
      }

      if (signatureDateFilter) {
        if (signatureDate) {
          const momentSD = moment(signatureDate);
          const [startValue, endValue] = signatureDateFilter;
          return momentSD.isBefore(endValue) && momentSD.isAfter(startValue);
        }
        return false;
      }

      if (escalatedFilter) {
        return escalated === true;
      }

      if (isBetaFilter) {
        return isBeta === true; // Amazin'
      }

      return true;
    },
  );
  return sortData(filteredResources);
}


function HouseholdSummaryPageView({
  filterCustodian,
  filterIsBeta,
  filterMilestone,
  filterPaymentMethod,
  filterMilestoneDate,
  filterLastMeeting,
  filterSignedPADate,
  filterStatus,
  filterEscalated,
  setHouseholdId,
  householdSummaryResp,
  ...rest
}) {
  const { rootNodeProps, logEvent } = useEventTrackingLogEvent({
    nodeName: 'Household Summary',
    isPageView: true,
  });

  const [state, setState] = React.useState({
    custodian: filterCustodian || '',
    isBeta: filterIsBeta || false,
    milestone: filterMilestone || '',
    paymentMethod: filterPaymentMethod || '',
    milestoneDate: filterMilestoneDate || null,
    lastMeetingDate: filterLastMeeting || null,
    signatureDate: filterSignedPADate || null,
    status: filterStatus || [],
    escalated: filterEscalated || false,
    loading: false,
  });

  function onCreateHousehold() {
    logEvent('Clicked Add Client');
    const params = decodeQuery(history.location.search);
    params.modal = 'createhh';
    history.push({
      pathname: history.location.pathname,
      search: encodeQuery(params),
    });
  }

  const onRowClick = (_, row, column) => {
    if (!row) return () => {};

    return {
      onClick: (e) => {
        e.preventDefault();
        logEvent('Household Summary Page: Clicked Table');
        const householdId = row.original.id();
        if (column.id === 'actionItemCount') {
          history.push(
            planningUri
              + encodeQuery({
                filterText: `${row.original.attr('clientFirstName')} ${row.original.attr('clientLastName')}`,
              }),
          );
        }

        // open modal
        openModal(modalConsts.household, [{ householdId }]);

        setHouseholdId(householdId);
      },
    };
  };
  const buildQueryString = () => `?${Object.keys(state)
    .filter(k => !!state[k]) // skip falsy values
    .filter(
      k => (!Array.isArray(state[k]) || state[k].length > 0) // skip empty arrays
                && k !== 'householdSummaries',
    )
    .map(k => `${encodeURIComponent(k)}=${encodeURIComponent(state[k])}`)
    .join('&')}`;


  const setFilter = (name, value) => {
    const newState = { ...state, [name]: value };
    setState(newState, () => updateQueryString(buildQueryString()));
  };

  return (
    <div
      {...rootNodeProps}
      className="HouseholdSummaryPage padding-2"
      {...rest}
    >
      <div className="upper">
        <div className="count">{`${(householdSummaryResp) ? householdSummaryResp.resources().length() : '?'} Clients`}</div>
        <div
          className="add-client"
          onClick={onCreateHousehold}
        >
          <IconButton
            icon="addIcon"
            test="addnewclient"
            onClick={onCreateHousehold}
          />
            Add Client
        </div>
      </div>
      <div className="lower">
        <Filters {...state} onFilterChange={setFilter} />
        <DataTable
          className="ReactTable"
          columns={columns}
          data={filterHouseholdSummaries(householdSummaryResp, state)}
          defaultFilterMethod={filterByContains}
          defaultPageSize={50}
          filterable
          getTdProps={onRowClick}
          minRows={10}
          pageSizeOptions={[50, 100, 150]}
          PaginationComponent={PagingLinksWithViewsButtons}
          onVisibleCountChange={count => setState({ ...state, clientCount: count })}
        />
      </div>
    </div>
  );
}

HouseholdSummaryPageView.propTypes = {
  filterIsBeta: PropTypes.bool,
  filterStatus: PropTypes.arrayOf(PropTypes.string),
  filterMilestone: PropTypes.string,
  filterLastMeeting: PropTypes.string,
  filterCustodian: PropTypes.string,
  filterPaymentMethod: PropTypes.string,
  filterMilestoneDate: PropTypes.arrayOf(moment),
  filterSignedPADate: PropTypes.arrayOf(moment),
  filterEscalated: PropTypes.bool,
  setHouseholdId: PropTypes.func.isRequired,
  householdSummaryResp: PropTypes.object,
};

HouseholdSummaryPageView.defaultProps = {
  filterIsBeta: false,
  filterStatus: [],
  filterMilestone: '',
  filterLastMeeting: '',
  filterCustodian: '',
  filterPaymentMethod: '',
  filterMilestoneDate: null,
  filterSignedPADate: null,
  filterEscalated: false,
  householdSummaryResp: null,
};

export default HouseholdSummaryPageView;
